import classNames from 'classnames';
import Multiselect from 'multiselect-react-dropdown';
import * as React from 'react';
import { useRef, useState } from 'react';

import { SearchProductSkuResult } from '../../../../Features/Filtering/Api/SearchProductSkuResult.csharp';
import { getFor } from '../../../../Features/ReactComponents/API';
import { FormInputValidationIssues } from '../../../../Features/ReactComponents/Form/FormInputValidationIssues';
import { FormField, ValidationIssue, ValidationType } from '../../../../Features/ReactComponents/Form/FormInterfaces';
import { IValidationResponse } from '../../Common/IValidationResponse.csharp';

interface FormSelectProps {
    getProductSkusUrl: string;
    input: FormField;
    validate: (skus: string[]) => Promise<IValidationResponse>;
    // eslint-disable-next-line
    dependencies?: any[];
    afterValidateItems?: (validationResponse: IValidationResponse) => void;
    // eslint-disable-next-line
    onChange: (value: any, name: string, errors: ValidationIssue[]) => void;
    inputName: string;
}

export const SkuMultiSelect = (props: FormSelectProps) => {
    const { input } = props;
    const minTriggerInputCharNumber = 3;
    const fetchingTimeout = 300;
    const fetchTimeout = useRef<NodeJS.Timeout>();

    const [suggestions, setSuggestions] = useState<SearchProductSkuResult[]>([]);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [validationIssues, setValidationIssues] = useState<ValidationIssue[]>([]);
    const [loading, setLoading] = useState(false);
    const [isRequired] = useState<boolean>(input.validation?.required?.value ?? false);

    React.useEffect(() => {
        validateItems(selectedItems);
    }, [selectedItems, ...(props.dependencies ?? [])]);

    const getValidationIssueClass = (): string => {
        return validationIssues && validationIssues.length > 0
            ? validationIssues[0].type == ValidationType.Warning
                ? 'NewForm__Warning'
                : 'NewForm__Error'
            : '';
    };
    const fetchSuggestions = (value: string) => {
        if (value.length >= minTriggerInputCharNumber) {
            if (fetchTimeout.current !== undefined) {
                clearTimeout(fetchTimeout.current);
            }
            fetchTimeout.current = setTimeout(() => {
                setLoading(true);
                getFor<SearchProductSkuResult[]>(`${props.getProductSkusUrl}?searchPhrase=${value}`)
                    .then((result) => {
                        setSuggestions(result);
                        setLoading(false);
                    })
                    .catch((error: Response) => {
                        error.text().then((errorMessage: string) => {
                            createValidationIssue(errorMessage, ValidationType.Error);
                        });
                    });
            }, fetchingTimeout);
        }
    };

    const validateItems = (items: string[]) => {
        if (items.length > 0 || isRequired) {
            props
                .validate(items)
                .then((response) => {
                    if (response.errors || response.warnings) {
                        const validationIssues: ValidationIssue[] = [];
                        validationIssues.push(...response.warnings.map((warning) => ({ message: warning, type: ValidationType.Warning } as ValidationIssue)));
                        validationIssues.push(...response.errors.map((error) => ({ message: error, type: ValidationType.Error } as ValidationIssue)));
                        setValidationIssues(validationIssues);
                    } else {
                        setValidationIssues([]);
                    }

                    if (props.afterValidateItems) props.afterValidateItems(response);
                })
                .catch((error: Response) => {
                    error.text().then((errorMessage: string) => {
                        createValidationIssue(errorMessage, ValidationType.Error);
                    });
                });
        } else {
            setValidationIssues([]);
            input.value = [];
        }
    };

    const createValidationIssue = (errorMessage: string, validationIssueType: ValidationType) => {
        const message = JSON.parse(errorMessage).message;
        const validationIssue: ValidationIssue[] = [{ message: message, type: validationIssueType }];
        setValidationIssues(validationIssue);
    };

    const addSelectedOption = (list: string[], value: SearchProductSkuResult) => {
        setSelectedItems((selectedOptions) => [...selectedOptions, value.variantSku]);
        props.onChange(selectedItems, props.inputName, validationIssues);
    };

    const removeSelectedOption = (list: string[], value: SearchProductSkuResult) => {
        setSelectedItems((selectedOptions) => selectedOptions.filter((element) => element !== value.variantSku));
        props.onChange(selectedItems, props.inputName, validationIssues);
    };

    return (
        <div className={classNames('NewForm__FieldWrapper', getValidationIssueClass())}>
            <>
                <div className="NewForm__MultiSelectField">
                    <label htmlFor={props.inputName}>{`SKUs ${isRequired ? '' : '(optional)'}`}</label>
                    <Multiselect
                        className="ReactMultiSelectDropdown"
                        style={{ chips: { background: '#dc143c' }, option: { highlightOption: { background: '#dc143c' } } }}
                        id="autocomplete"
                        options={suggestions}
                        displayValue={'variantSku'}
                        onSelect={addSelectedOption}
                        onRemove={removeSelectedOption}
                        onSearch={(value) => fetchSuggestions(value)}
                        hidePlaceholder
                        emptyRecordMsg={'Type first 3 numbers to see suggestions'}
                        loading={loading}
                    />
                </div>
                <FormInputValidationIssues validationIssue={validationIssues} />
            </>
        </div>
    );
};
